import { normalizeAngle, meanMotion, positionAtMeanAnomaly } from '../core/kepler';
import type { OrbitalElements } from '../core/types';

/**
 * Phase helpers for the direct-manipulation model (Story 1.7, FR-19). A pure,
 * framework-free `state`-layer module: it may import `core` (state -> core is
 * allowed) but nothing from `render`/`ui`. All quantities are mean-anomaly RADIANS
 * (AD-5): no AU, day, or degree literal appears here.
 */

/**
 * The single shared phase-step increment, in mean-anomaly radians. BOTH the
 * keyboard step and the single-pointer "-"/"+" step controls draw their magnitude
 * from THIS one constant so they raise the identical `NudgePhase` payload as each
 * other (AD-2: "one shared increment constant"); the pointer drag raises the same
 * command with a geometrically-computed magnitude. Default: pi/18 = a 10-degree
 * mean-anomaly step. Tunable, but never duplicated.
 */
export const PHASE_STEP_RADIANS = Math.PI / 18;

/**
 * Wrap a mean anomaly into the canonical range [0, 2*pi). Delegates to the pure
 * `core` `normalizeAngle` (single source of the modulo, AD-1), so `state` and
 * `core` never drift on the wrap convention. Used when applying `NudgePhase` so
 * the accumulated offset stays bounded.
 */
export function normalizeMeanAnomaly(m: number): number {
  return normalizeAngle(m);
}

/**
 * The single place a body's base mean anomaly and its dragged offset combine, so
 * renderer, readouts, and hit-test all agree: `normalizeMeanAnomaly(baseM + offset)`.
 */
export function effectiveMeanAnomaly(baseM: number, offset: number): number {
  return normalizeMeanAnomaly(baseM + offset);
}

/**
 * Effective mean anomaly of an orbiting body at simulation time `timeSeconds`
 * under a dragged phase `offset`: base mean anomaly `n*t` (frozen at 0 at time
 * zero) shifted by the offset and wrapped. The base uses the CENTRAL body's mu
 * (`muCentral`), never the orbiting body's (PRD 5.3). Dragging shifts phase
 * WITHOUT advancing `time`; the offset lives in state, `time` is untouched.
 */
export function effectiveMeanAnomalyAtTime(
  a: number,
  muCentral: number,
  timeSeconds: number,
  offset: number
): number {
  const baseM = timeSeconds === 0 || a === 0 ? 0 : meanMotion(a, muCentral) * timeSeconds;
  return effectiveMeanAnomaly(baseM, offset);
}

/**
 * Focus-centred SI world position of an orbiting body at `timeSeconds` under a
 * dragged phase `offset`. Combines `effectiveMeanAnomalyAtTime` with the pure core
 * perifocal solver (`positionAtMeanAnomaly`), so the renderer, the readouts, and
 * the ui pointer hit-test share ONE effective-position path (no drift).
 */
export function effectiveWorldPosition(
  elements: OrbitalElements,
  muCentral: number,
  timeSeconds: number,
  offset: number
): { x: number; y: number } {
  const m = effectiveMeanAnomalyAtTime(elements.a, muCentral, timeSeconds, offset);
  return positionAtMeanAnomaly(elements, m);
}
